/**
 * Cashier offline action queue.
 *
 * UUID-keyed IndexedDB store for state-changing requests (POST/PUT/PATCH),
 * mirroring the driver-pwa D36 offline-first contract:
 *
 *  conflict replay (409/422): surface to the app via onConflict, mark failed.
 *         Never silent-drop, never infinite-retry.
 *
 *  auth-expiry replay (401): call onAuthExpired (shows re-login UI), then
 *         retry the request once with the new token. Do NOT drop the queue.
 *
 * Endpoints are generic ({ method, endpoint, body }) rather than domain-typed
 * since the screens that produce them (order entry, product lookup, receive
 * stock) land in the 1b screens bench.
 */
import { openCashierDb, STORES } from './db'
import {
  API_PREFIX,
  requestHeaders,
  currentSub,
  readLiveSessionUserId,
  refreshSession,
  isPasswordChangeRequired,
  announcePasswordChangeRequired,
} from './auth'
import type { HttpMethod, QueuedRequest, QueuedRequestBody } from '../types'

/**
 * `attempts` tracks 5xx retry counts for the replay taxonomy below. It lives only as a local
 * augmentation of the canonical `QueuedRequest` (types.ts is outside this bench's files-locked
 * scope) — IndexedDB is schemaless, so widening the on-disk shape here doesn't touch that file.
 */
type StoredAction = QueuedRequest & { attempts?: number }

/** Give up on a transient (5xx) failure after this many replay attempts. */
export const MAX_ATTEMPTS = 5

export interface ReplayOpts {
  /** Called when the server rejects the request (409/422) — no silent-drop. */
  onConflict: (action: QueuedRequest, response: Response) => void
  /** Called on 401; must resolve only after the cashier has re-authenticated. */
  onAuthExpired: () => Promise<void>
  /** Called on 403 — a stale/absent CSRF nonce. Distinct from 401: the SESSION is fine. */
  onCsrfRejected?: (action: QueuedRequest) => void
}

// ── IndexedDB helpers ─────────────────────────────────────────────────────────

async function putAction(action: QueuedRequest): Promise<void> {
  const db = await openCashierDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.QUEUE, 'readwrite')
    const req = tx.objectStore(STORES.QUEUE).put(action)
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error)
  })
}

export async function enqueueRequest(
  method: HttpMethod,
  endpoint: string,
  body: QueuedRequestBody,
): Promise<string> {
  const uuid = crypto.randomUUID()
  const action: QueuedRequest = {
    uuid,
    method,
    endpoint,
    body,
    status: 'pending',
    createdAt: Date.now(),
  }
  // R413: bound to the person signed in now; with nobody signed in there is no stamp.
  const sub = currentSub()
  if (sub) action.sub = sub
  await putAction(action)
  return uuid
}

export async function getPendingRequests(): Promise<QueuedRequest[]> {
  const db = await openCashierDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.QUEUE, 'readonly')
    const req = tx.objectStore(STORES.QUEUE).getAll()
    req.onsuccess = () =>
      resolve((req.result as QueuedRequest[]).filter((a) => a.status === 'pending'))
    req.onerror = () => reject(req.error)
  })
}

async function markSent(uuid: string): Promise<void> {
  const db = await openCashierDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.QUEUE, 'readwrite')
    const store = tx.objectStore(STORES.QUEUE)
    const getReq = store.get(uuid)
    getReq.onsuccess = () => {
      const item = getReq.result as QueuedRequest | undefined
      if (item) store.put({ ...item, status: 'sent' })
      resolve()
    }
    getReq.onerror = () => reject(getReq.error)
  })
}

async function markFailed(uuid: string, reason: string): Promise<void> {
  const db = await openCashierDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.QUEUE, 'readwrite')
    const store = tx.objectStore(STORES.QUEUE)
    const getReq = store.get(uuid)
    getReq.onsuccess = () => {
      const item = getReq.result as QueuedRequest | undefined
      if (item) store.put({ ...item, status: 'failed', failReason: reason })
      resolve()
    }
    getReq.onerror = () => reject(getReq.error)
  })
}

async function patchAttempts(uuid: string, attempts: number): Promise<void> {
  const db = await openCashierDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.QUEUE, 'readwrite')
    const store = tx.objectStore(STORES.QUEUE)
    const getReq = store.get(uuid)
    getReq.onsuccess = () => {
      const item = getReq.result as StoredAction | undefined
      if (item) store.put({ ...item, attempts })
      resolve()
    }
    getReq.onerror = () => reject(getReq.error)
  })
}

// ── Core replay ───────────────────────────────────────────────────────────────

async function executeOne(action: QueuedRequest): Promise<Response | null> {
  try {
    return await fetch(`${API_PREFIX}${action.endpoint}`, {
      method: action.method,
      headers: requestHeaders(action.method, true),
      body: JSON.stringify(action.body),
    })
  } catch {
    // Network error — leave pending
    return null
  }
}

/**
 * Drain the pending queue.
 *
 * For each pending action:
 *   200         → mark sent (idempotent replay: re-sent = same one effect via CAS)
 *   409/422     → reconcile + surface, mark failed (no silent-drop, no retry)
 *   401         → trigger re-auth, retry once, then mark sent or leave pending
 *   403         → surface CSRF rejection, leave pending (session is fine, nonce isn't)
 *   4xx (other) → terminal, mark failed. A 4xx will not become a 2xx by being
 *                 retried — bench-CASHIER-QUEUE-TRUTH.1, ported from idb-queue.ts §3.3
 *   5xx         → retried up to MAX_ATTEMPTS, then mark failed
 *   network err → leave pending (will retry on next replay trigger)
 */
export async function replayQueue(opts: ReplayOpts): Promise<void> {
  // R413: the till is shared. Only the signed-in person's requests are sent; nobody signed
  // in sends nothing; others (and unstamped ones) stay pending with attempts unchanged.
  //
  // Two-tab binding (Frank #63294): the requests carry whatever HttpOnly cookie the
  // browser holds NOW, which another tab may have signed someone else into. Before
  // sending anything, the live session (GET /auth/session) must name the same person
  // this tab holds in memory; a failed read sends nothing (never fall back to memory).
  // The window between this read and the sends below stays open; closing it needs the
  // gateway. This check covers the common two-tab case, not that race.
  // When the live session names someone else, this tab adopts it (Rook #63300); a failed
  // read adopts nothing.
  const owner = currentSub()
  if (!owner) return // nobody signed in: send nothing
  const live = await readLiveSessionUserId()
  if (live !== null && live !== owner) {
    await refreshSession() // another person's cookie: adopt their session, send nothing
    return
  }
  if (live !== owner) return // unknown (read failed or no session): send nothing

  const pending = (await getPendingRequests()).filter((a) => a.sub === owner)

  for (const action of pending) {
    const res = await executeOne(action)

    if (res === null) continue // network error — leave pending

    if (res.ok) {
      await markSent(action.uuid)
      continue
    }

    if (res.status === 409 || res.status === 422) {
      opts.onConflict(action, res)
      await markFailed(action.uuid, `conflict:${res.status}`)
      continue
    }

    if (res.status === 401) {
      await opts.onAuthExpired()
      // A different cashier may have signed in: re-read the live cookie's user and send
      // the retry only when it is this tab's session and owns the action. Otherwise the
      // action stays pending and the pass ends: every later send would carry that same
      // cookie, which no longer names this pass's owner.
      const relive = await readLiveSessionUserId()
      if (relive === null || relive !== currentSub() || relive !== action.sub) return
      const retryRes = await executeOne(action)
      if (retryRes !== null && retryRes.ok) {
        await markSent(action.uuid)
      }
      // If retry still fails or errors, leave pending (cashier can try again after re-auth).
      continue
    }

    if (res.status === 403 && (await isPasswordChangeRequired(res))) {
      // R413: not a failure. The person must choose their own password first: leave this
      // and the rest pending (attempts unchanged), stop the drain, show the change screen.
      announcePasswordChangeRequired()
      return
    }

    if (res.status === 403) {
      // Stale or absent CSRF nonce. The SESSION is fine, so this is NOT onAuthExpired --
      // treating it as one would log the cashier out mid-shift for a recoverable nonce miss.
      // Surface it and leave the action pending; a session refresh re-mints the nonce.
      opts.onCsrfRejected?.(action)
      continue
    }

    if (res.status < 500) {
      // bench-CASHIER-QUEUE-TRUTH.1 — ported from hop-pwa's idb-queue.ts (bench §3.3/§3.4):
      // "A 4xx will not become a 2xx by being retried: that is terminal, and retrying it
      // forever is precisely the bug." This branch used to be
      // `// Other (5xx, unexpected) — leave pending for next replay cycle`, which caught any
      // 4xx not already special-cased above (404 chief among them) and retried it forever —
      // a poison entry that reported as pending money and was surfaced never. A pre-fix
      // `apiSend` could still have enqueued one of these; this reclassifies it terminal on the
      // very next replay cycle instead of leaving it pending indefinitely.
      await markFailed(action.uuid, `rejected:${res.status}`)
      continue
    }

    // 5xx: may be transient. Retry a bounded number of times, then give up — same taxonomy,
    // same MAX_ATTEMPTS semantics as idb-queue.ts.
    const attempts = ((action as StoredAction).attempts ?? 0) + 1
    if (attempts >= MAX_ATTEMPTS) {
      await markFailed(action.uuid, `server-error:${res.status}`)
    } else {
      await patchAttempts(action.uuid, attempts)
    }
  }
}
